/**
 * API Client
 * 
 * Handles all HTTP requests to our backend.
 * Automatically includes our JWT in all requests.
 */

// In development, use relative URLs to leverage the proxy
// In production, use the full API URL from env or default
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'http://localhost:5001' : '');

// Storage key for token
const TOKEN_KEY = 'asset_track_token';

/**
 * Get the current auth token
 */
function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Make an authenticated API request
 * 
 * @param {string} endpoint - API endpoint (e.g., '/auth/me')
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} Response data
 */
async function request(endpoint, options = {}) {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  // Add auth header if we have a token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers
  };

  // Use full URL if API_BASE_URL is set, otherwise use relative URL (for proxy)
  const url = API_BASE_URL ? `${API_BASE_URL}${endpoint}` : endpoint;
  const response = await fetch(url, config);
  
  // Handle non-JSON responses
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.text();
  }

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.message || 'Request failed');
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

/**
 * Make a file upload request (multipart/form-data)
 * 
 * @param {string} endpoint - API endpoint
 * @param {FormData} formData - Form data with files
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<any>} Response data
 */
async function uploadRequest(endpoint, formData, onProgress = null) {
  const token = getToken();
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    const url = API_BASE_URL ? `${API_BASE_URL}${endpoint}` : endpoint;
    xhr.open('POST', url);
    
    // Add auth header
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    
    // Progress tracking
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete, event.loaded, event.total);
        }
      };
    }
    
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          const error = new Error(data.message || 'Upload failed');
          error.status = xhr.status;
          error.data = data;
          reject(error);
        }
      } catch (e) {
        reject(new Error('Invalid response from server'));
      }
    };
    
    xhr.onerror = () => {
      reject(new Error('Network error during upload'));
    };
    
    xhr.send(formData);
  });
}

/**
 * API methods
 */
const api = {
  /**
   * GET request
   */
  get: (endpoint, options = {}) => {
    return request(endpoint, { ...options, method: 'GET' });
  },

  /**
   * POST request
   */
  post: (endpoint, body, options = {}) => {
    return request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body)
    });
  },

  /**
   * PUT request
   */
  put: (endpoint, body, options = {}) => {
    return request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body)
    });
  },

  /**
   * PATCH request
   */
  patch: (endpoint, body, options = {}) => {
    return request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  },

  /**
   * DELETE request
   */
  delete: (endpoint, options = {}) => {
    return request(endpoint, { ...options, method: 'DELETE' });
  },

  /**
   * DELETE request with body
   */
  deleteWithBody: (endpoint, body, options = {}) => {
    return request(endpoint, {
      ...options,
      method: 'DELETE',
      body: JSON.stringify(body)
    });
  },

  /**
   * Upload files (multipart/form-data)
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - Form data with files
   * @param {Function} onProgress - Optional progress callback (percent, loaded, total)
   */
  upload: (endpoint, formData, onProgress = null) => {
    return uploadRequest(endpoint, formData, onProgress);
  },

  /**
   * Set the auth token (used after login)
   */
  setToken: (token) => {
    localStorage.setItem(TOKEN_KEY, token);
  },

  /**
   * Clear the auth token (used after logout)
   */
  clearToken: () => {
    localStorage.removeItem(TOKEN_KEY);
  },

  /**
   * Check if we have a token
   */
  hasToken: () => {
    return Boolean(getToken());
  }
};

export default api;

